import { ActivityObject } from "./activity-types";
import { getLocalAsset } from "./native";
import {
  DifficultyLevel,
  WorkoutObject,
  ExerciseWithOptions,
  ExerciseObject,
  CustomWorkout,
  WorkoutLog,
  PlayerObject,
  LearningLibraryObject,
  LearningLibraryLog,
  SessionObject,
  AssessmentObject,
} from "@sworkit/api";
import { jsVersionWithBuildNumber, swApi } from "./app";
import { Capacitor } from "@capacitor/core";
import {
  createDateAsUTC,
  js_yyyy_mm_dd_hh_mm_ss,
} from "../actions/workout-logs";
import { getDeviceType } from "./sw-device";
import { collection, doc } from "firebase/firestore";
import { fst as firestore } from "../global/firebase";
import {
  markCollectionWorkoutComplete,
  markCollectionWorkoutIncomplete,
} from "./firestore-workouts-completed-collection";
import { translate } from "./translate";
import { secondsToMinutes } from "date-fns";

export const meditationsBackgroundImageBaseUrl = `https://storage.googleapis.com/sworkit-assets/images/meditations/`;
export const nutritionBackgroundImageBaseUrl = `https://storage.googleapis.com/sworkit-assets/images/learning-library/`;

export function isTodaysWorkout(collectionSlug: string, index: number) {
  if (!collectionSlug) return false;
  const now = new Date();
  const today = now.getDate();
  const thisMonth = now.getMonth();
  const isMonth = getMonthIndexFromSlug(collectionSlug) === thisMonth;
  if (!isMonth) return false;
  const isToday = today == index + 1;
  return isToday;
}

export function getCollectionSlugForMonthIndex(monthIndex: number) {
  return `${months[monthIndex]}-challenge`;
}

function getMonthIndexFromSlug(slug: string) {
  let monthIndex;
  for (let i = 0; i < months.length; i++) {
    if (slug.includes(months[i])) {
      monthIndex = i;
      break;
    }
  }
  return monthIndex;
}

const months = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
];

export function calculateCalories(
  weightInLbs: number,
  mins: number,
  activityIntensity: ActivityObject["activityIntensity"],
  difficulty: DifficultyLevel,
) {
  const weight = weightInLbs || 150;
  const kg = weight / 2.2;
  let intensity = 1;
  if (difficulty == 1) {
    intensity = 0.75;
  } else if (difficulty == 3) {
    intensity = 1.25;
  }
  const calories = Math.round((intensity * activityIntensity * kg * mins) / 60);
  console.debug("kg", kg, intensity, mins, calories);
  return calories;
}

export function convertHydratedWorkoutIntoCustomWorkout(
  workout: WorkoutObject,
) {
  const exerciseSlugs: string[] = [];
  const exercisesWithOptions: ExerciseWithOptions[] = [];
  (workout.exercises as ExerciseObject[]).map((exercise) => {
    exerciseSlugs.push(exercise.slug);
    const exerciseWithOptions: ExerciseWithOptions = {
      slug: exercise.slug,
      timing: exercise.timing,
      exerciseLength: exercise.exerciseLength,
      repCount: exercise.repCount,
    };
    exercisesWithOptions.push(exerciseWithOptions);
  });
  const editWorkout: CustomWorkout = {
    category: workout.category,
    description: workout.description,
    timingStyle: workout.timingStyle ? workout.timingStyle : "standard",
    exercisesWithOptions,
    name: workout.name,
    workout: exerciseSlugs,
    deleted: false,
    allowRestBreaks: workout.allowRestBreaks,
    allowShuffle: workout.allowShuffle,
    allowWarmup: workout.allowWarmup,
  };
  return editWorkout;
}

export function getDefaultBackHref(collectionSlug: string) {
  if (collectionSlug === "recommended") return "/my-sworkit";
  if (isCollectionSlugPlan(collectionSlug)) return `/goals/${collectionSlug}`;
  if (collectionSlug === "custom") return "/custom";
  if (collectionSlug === "favorites") return "/favorites";
  if (collectionSlug === "workout-library") return "/workout-library";
  return `/collections/${collectionSlug}`;
}

export function getLogImage(workoutLog: WorkoutLog) {
  const { slug, type, logType } = workoutLog || {};
  if (slug && isWorkoutSlugCustom(slug)) {
    return "/assets/img/plan-custom.jpg";
  }
  if (slug) {
    const baseURL = "https://storage.googleapis.com/sworkit-assets/images";
    return `${baseURL}/workouts/${slug}.jpg`;
  }
  if (logType === "apple-health") {
    return "/assets/img/apps/apple-health-icon.svg";
  }
  if (logType === "google-fit") {
    return "/assets/img/apps/google-fit-icon.svg";
  }
  if (logType === "health-connect") {
    return "/assets/img/apps/health-connect-icon.svg";
  }
  if (logType === "fitbit") {
    return "/assets/img/apps/fitbit-icon.svg";
  }
  const image = getImageFromType(type);
  return image;
}

function getImageFromType(type: string) {
  if (!type) return null;
  type = type.toLowerCase();
  if (type.includes("strength")) {
    return "/assets/img/category-strength.jpg";
  } else if (type.includes("yoga")) {
    return "/assets/img/category-yoga.jpg";
  } else if (type.includes("stretch")) {
    return "/assets/img/category-stretch.jpg";
  } else if (type.includes("cardio")) {
    return "/assets/img/category-cardio.jpg";
  } else if (type.includes("leaner")) {
    return "/assets/img/plan-leaner.jpg";
  } else if (type.includes("fitter")) {
    return "/assets/img/plan-fitter.jpg";
  } else if (type.includes("stronger")) {
    return "/assets/img/plan-stronger.jpg";
  } else {
    return "/assets/img/plan-custom.jpg";
  }
}

export function errorLoadingWorkoutImage(e) {
  console.error("error loading workout log image", e.target.src);
  e.target.src = "/assets/img/plan-custom.jpg";
}

export function isWorkoutSlugCustom(workoutSlug: string): boolean {
  return workoutSlug && workoutSlug !== null && workoutSlug.startsWith("-");
}

const localCustomImage = "/assets/img/plan-custom.jpg";
export function getImageUrlFromSlug(slug: string, collectionLocation?: string) {
  if (isWorkoutSlugCustom(slug)) {
    return localCustomImage;
  } else {
    let collectionFolder = collectionLocation || "workouts";
    //todo once we're not using dataType 'workout' remove this
    if (collectionLocation === "workout") {
      collectionFolder = "workouts";
    }
    if (collectionLocation === "learningLibrary") {
      collectionFolder = "learning-library";
    }
    if (collectionLocation === "studioWorkouts") {
      collectionFolder = "workouts";
    }
    if (collectionLocation === "collections") {
      collectionFolder = "series";
    }

    // console.debug(
    //   'getting image',
    //   `https://storage.googleapis.com/sworkit-assets/images/${collectionFolder}/${slug}.jpg`,
    // );
    return `https://storage.googleapis.com/sworkit-assets/images/${collectionFolder}/${slug}.jpg`;
  }
}

export function getLocalMeditationBackgroundImageFromSlug(workoutSlug: string) {
  return getLocalAsset(
    `${meditationsBackgroundImageBaseUrl}${workoutSlug}.jpg`,
  );
}

export function getLocalLearningLibraryBackgroundImageFromSlug(
  workoutSlug: string,
) {
  return getLocalAsset(`${nutritionBackgroundImageBaseUrl}${workoutSlug}.jpg`);
}

/**
 * cache our gcs files on native,
 * remote address `https://storage.googleapis.com/sworkit-assets/images/${collectionLocation}/${slug}.jpg`;
 * @param slug
 * @param collectionLocation
 * @returns
 */
export function getLocalImageFromSlug(
  slug: string,
  collectionLocation?: string,
) {
  if (isWorkoutSlugCustom(slug)) {
    return localCustomImage;
  } else {
    return getLocalAsset(getImageUrlFromSlug(slug, collectionLocation));
  }
}

export const plannedWorkoutSlugs = [
  "fitter-beginner",
  "fitter-intermediate",
  "fitter-advanced",
  "leaner-beginner",
  "leaner-intermediate",
  "leaner-advanced",
  "stronger-beginner",
  "stronger-intermediate",
  "stronger-advanced",
];

export function isCollectionSlugPlan(slug: string) {
  return plannedWorkoutSlugs.indexOf(slug) !== -1;
}

export async function getWorkout(workoutSlug: string): Promise<SessionObject> {
  if (isWorkoutSlugCustom(workoutSlug)) {
    const customWorkout = await swApi.customWorkout.get(workoutSlug);
    customWorkout.slug = workoutSlug;
    return customWorkout;
  } else {
    return swApi.search(workoutSlug) as SessionObject;
  }
}

export async function getAnyFromSlug(
  workoutSlug: string,
): Promise<SessionObject | AssessmentObject> {
  if (isWorkoutSlugCustom(workoutSlug)) {
    const customWorkout = await swApi.customWorkout.get(workoutSlug);
    customWorkout.slug = workoutSlug;
    return customWorkout;
  } else {
    return swApi.search(workoutSlug) as SessionObject;
  }
}

export function getLearningLibrary(workoutSlug: string) {
  return swApi.search(workoutSlug);
}

export async function getWorkoutsFromSlugs(workoutSlugs: string[]) {
  const workouts = workoutSlugs.map((slug) => swApi.search(slug));

  return workouts.filter((workout) => (workout && workout.slug ? true : false));
}
export const getMinutesFromSecondsAsRoundedToNearestHalf = (
  seconds: number,
) => {
  return Math.round(2 * (seconds / 60)) / 2;
};
export function secondsToHms(d: number): string {
  d = d < 0 ? 0 : Number(d);
  var h = Math.floor(d / 3600);
  var m = Math.floor((d % 3600) / 60);
  var s = Math.floor((d % 3600) % 60);
  return (
    (h > 0 ? h + ":" + (m < 10 ? "0" : "") : "") +
    m +
    ":" +
    (s < 10 ? "0" : "") +
    s
  );
}

export function getCalories(
  userWeightInLbs: number = 175,
  activityWeight: number = 4,
  timeElapsedInSeconds: number,
) {
  return Math.round(
    (activityWeight * userWeightInLbs * timeElapsedInSeconds) / 60 / 60,
  );
}

export async function getMeditationLog(
  workout: PlayerObject,
  collectionSlug: string,
  endTime: Date,
  timeElapsed: number,
  weightInLbs: number,
  connectedApps,
  // shouldSaveToHealthIntegrations: HealthIntegrationKey[],
) {
  const { activityKey, name, activityWeight, slug, category } = workout;
  const createdOn = js_yyyy_mm_dd_hh_mm_ss(endTime);
  const utcCreated = js_yyyy_mm_dd_hh_mm_ss(createDateAsUTC(endTime));
  const appVersion = jsVersionWithBuildNumber;
  const deviceType = await getDeviceType();
  const timestampMillis = endTime.valueOf();
  const minutesCompleted =
    getMinutesFromSecondsAsRoundedToNearestHalf(timeElapsed);
  const calories = getCalories(weightInLbs, activityWeight, timeElapsed);
  let id = null;
  if (firestore) {
    const ref = doc(collection(firestore, "workoutLogs"));
    id = ref.id;
  }
  const newLog: WorkoutLog = {
    id,
    activityKey,
    appVersion,
    platform: Capacitor.getPlatform() as "ios" | "android" | "web",
    calories,
    connectedApps,
    createdOn,
    heartRate: "",
    slug,
    minutesCompleted,
    deviceType,
    name,
    collectionSlug,
    timestampMillis,
    type: "meditation",
    utcCreated,
    shouldSaveToHealth: true,
    savedToHealth: false,
    savedToHealthIntegrations: [],
    category,
    logType: "sworkout",
    externalUuid: null,
    assignmentIds: [],
  };
  return newLog;
}

export async function getLearningLibraryLog(
  content: LearningLibraryObject,
  collectionSlug: string,
  endTime: Date,
  minutesCompleted: number,
) {
  const { name, slug, category } = content;
  const createdOn = js_yyyy_mm_dd_hh_mm_ss(endTime);
  const utcCreated = js_yyyy_mm_dd_hh_mm_ss(createDateAsUTC(endTime));
  const appVersion = jsVersionWithBuildNumber;
  const deviceType = await getDeviceType();
  const timestampMillis = endTime.valueOf();
  let id = null;
  if (firestore) {
    const ref = doc(collection(firestore, "learningLibraryLogs"));
    id = ref.id;
  }
  const newLog: LearningLibraryLog = {
    id,
    appVersion,
    platform: Capacitor.getPlatform() as "ios" | "android" | "web",
    createdOn,
    slug,
    minutesCompleted,
    deviceType,
    name,
    collectionSlug,
    timestampMillis,
    type: category,
    utcCreated,
    category,
    logType: "content",
    assignmentIds: [],
  };
  return newLog;
}

export function longPressWorkout(
  collectionWorkoutsCompleted: { [workoutSlug: string]: boolean },
  collectionSlug: string,
  workoutSlug: string,
) {
  if (!collectionSlug || !workoutSlug) return;
  const completed = collectionWorkoutsCompleted[workoutSlug];
  if (completed) {
    markCollectionWorkoutIncomplete(collectionSlug, workoutSlug);
  } else {
    markCollectionWorkoutComplete(collectionSlug, workoutSlug);
  }
}

export function translateCircuitTime(workoutTimeLeft: string) {
  if (!workoutTimeLeft) return "";
  const timeLeftArr = workoutTimeLeft.split(":");
  while (timeLeftArr.length < 3) {
    timeLeftArr.unshift("0");
  }
  const [hour, min, sec] = timeLeftArr.map((string) => Number(string));
  return translate("ESTIMATED_TIME_HR_MIN_SEC", {
    hr: hour ? translate("SMART_HR", hour) : "",
    min: min ? translate("SMART_MIN", min) : "",
    sec: sec ? translate("SMART_SEC", sec) : "",
  });
}

export function displayTimeTranslationEstimation(seconds: number) {
  if (seconds < 36) {
    return `~ ${translate("SMART_SEC", 30)}`;
  }
  if (seconds < 60) {
    return `~ ${translate("SMART_MIN", 1)}`;
  }
  if (seconds < 3600) {
    const minutes = secondsToMinutes(seconds);
    return `~ ${translate("SMART_MIN", minutes)} `;
  }
  const minutes = secondsToMinutes(seconds);
  return `~ ${translate("SMART_MIN", minutes)}`;
}

export function titleCaseFromSlug(slug: string) {
  return slug
    ?.split("-")
    .map((word) => word[0].toLocaleUpperCase() + word.substring(1))
    .join(" ");
}
